import React, { useState, useEffect } from "react";
import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import _ from "lodash";
import { getToken, base_url } from "../../config/config";
import axios from "axios";

const assignmentsAPI = `${base_url}/v1/getAllAssignments`;
const projectsAPI = `${base_url}/v1/getAllProjects`;
const projectUsersAPI = `${base_url}/v1/project-users`;
const scheduleAPI = `${base_url}/v1/schedule`;

// Draggable project component using dnd-kit
function DraggableProject({ project }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: String(project._id),
    });
  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className={`mb-2 p-3 rounded-md border ${
        isDragging
          ? "border-indigo-300 bg-indigo-50 shadow-md"
          : "border-gray-200 bg-gray-50 hover:bg-gray-100"
      } transition-colors duration-150 ease-in-out`}
    >
      <div className="font-medium text-indigo-700 mb-1 flex items-center">
        {/* Planned indicator dot - green when hasUserProjects is true, red otherwise */}
        <span
          title={project.hasUserProjects ? "Is planned" : "Not planned"}
          aria-label={project.hasUserProjects ? "Is planned" : "Not planned"}
          className={`inline-block mr-2 rounded-full w-3 h-3 flex-shrink-0 ${
            project.hasUserProjects ? "bg-green-500" : "bg-red-500"
          }`}
        />
        <svg
          className="h-4 w-4 mr-1 text-indigo-500"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
        </svg>
        ANR: {project.ANR}
      </div>
      <div className="text-sm text-gray-600 mb-1 line-clamp-2">
        {project.Auftragsbezeichnung}
      </div>
      <div className="text-sm text-gray-500 flex items-center">
        <svg
          className="h-4 w-4 mr-1 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
            clipRule="evenodd"
          />
        </svg>
        Qty: {project.Menge}
      </div>
    </div>
  );
}

// Droppable container for the projects list
function DroppableProjects({ children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: "PROJECTS",
  });
  return (
    <div
      ref={setNodeRef}
      className={`p-2 min-h-[400px] max-h-[calc(100vh-260px)] overflow-y-auto ${
        isOver ? "bg-blue-50" : "bg-white"
      }`}
    >
      {children}
    </div>
  );
}

// Droppable cell for each assignment/day cell in the grid
function DroppableCell({ cellId, viewMode, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: cellId,
  });
  return (
    <div
      ref={setNodeRef}
      className={`${
        viewMode === "day"
          ? "h-16"
          : viewMode === "week"
          ? "h-20 min-h-[80px]"
          : "h-20 min-h-[60px]"
      } rounded-md ${
        isOver
          ? "bg-green-50/70 border-2 border-dashed border-green-200"
          : "border border-transparent hover:border-gray-200"
      }`}
    >
      {children}
    </div>
  );
}

// Component for a Gantt Chart bar
function GanttBar({ project, startPosition, duration, isSelected, onClick }) {
  return (
    <div
      className={`absolute h-8 rounded-md ${
        isSelected ? "bg-indigo-600" : "bg-indigo-500"
      } hover:bg-indigo-600 cursor-pointer transition-colors duration-150 ease-in-out`}
      style={{
        left: `${startPosition}%`,
        width: `${duration}%`,
        top: "4px",
      }}
      onClick={onClick}
    >
      <div className="px-2 py-1 text-xs font-medium text-white truncate">
        {project.ANR} - {project.Auftragsbezeichnung}
      </div>
    </div>
  );
}

// Time slot component for day view
function TimeSlot({ hour, minutes, cellId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: cellId,
  });

  return (
    <div
      ref={setNodeRef}
      className={`border-b border-gray-200 h-16 ${
        isOver
          ? "bg-green-50/70 border-2 border-dashed border-green-200"
          : "hover:bg-gray-50"
      }`}
    >
      <div className="flex h-full">
        <div className="w-16 flex-shrink-0 border-r border-gray-200 py-1 px-2 flex flex-col justify-center text-xs text-gray-500">
          {hour}:{minutes === 0 ? "00" : minutes}
        </div>
        <div className="flex-1 px-1 py-1">{children}</div>
      </div>
    </div>
  );
}

// Live Work Tracking Component
function LiveWorkTracking({ currentDate, viewMode }) {
  const [workData, setWorkData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAlerts, setShowAlerts] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedANR, setSelectedANR] = useState("");
  const [uniqueEmployees, setUniqueEmployees] = useState([]);
  const [uniqueANRs, setUniqueANRs] = useState([]);
  const [matchAnalysis, setMatchAnalysis] = useState({
    matched: 0,
    mismatched: 0,
    total: 0,
    matchPercentage: 0,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${base_url}/v1/getAllEmployees`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("Fetched employees:", response.data);

        if (response.data && Array.isArray(response.data)) {
          setEmployees(response.data);
        } else {
          console.error("Unexpected employee data format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchLiveWorkData();

    // Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchLiveWorkData();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [currentDate, viewMode]);

  const fetchLiveWorkData = async () => {
    setLoading(true);
    try {
      // Format date range based on view option
      const startDate = new Date(currentDate);
      const endDate = new Date(currentDate);

      if (viewMode === "week") {
        startDate.setDate(currentDate.getDate() - currentDate.getDay());
        endDate.setDate(startDate.getDate() + 6);
      } else if (viewMode === "month") {
        startDate.setDate(1);
        endDate.setMonth(currentDate.getMonth() + 1);
        endDate.setDate(0);
      }

      const start = startDate.toISOString().split("T")[0];
      const end = endDate.toISOString().split("T")[0];

      // Fetch project work data from your backend
      const response = await fetch(
        `${projectUsersAPI}?start=${start}&end=${end}`
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      // Compare work data with schedule to identify mismatches
      const compareWithSchedule = await fetch(
        `${scheduleAPI}?start=${start}&end=${end}`
      );
      const scheduleData = await compareWithSchedule.json();

      // Process and analyze the data
      const processedData = processWorkData(
        data.data || [],
        scheduleData.data || []
      );
      setWorkData(processedData);

      // Extract unique employees and projects
      const employees = [
        ...new Set(processedData.map((item) => item.user)),
      ].filter(Boolean);
      const anrs = [...new Set(processedData.map((item) => item.ANR))].filter(
        Boolean
      );

      setUniqueEmployees(employees);
      setUniqueANRs(anrs);

      // Calculate match statistics
      calculateMatchStatistics(processedData);
    } catch (err) {
      console.error("Error fetching work data:", err);
      setError("Failed to load work tracking data. Please try again later.");

      // For demonstration, set mock data if API fails
      const mockData = generateMockData();
      setWorkData(mockData);

      // Extract unique employees and projects from mock data
      const employees = [...new Set(mockData.map((item) => item.user))].filter(
        Boolean
      );
      const anrs = [...new Set(mockData.map((item) => item.ANR))].filter(
        Boolean
      );

      setUniqueEmployees(employees);
      setUniqueANRs(anrs);

      // Calculate match statistics for mock data
      calculateMatchStatistics(mockData);
    } finally {
      setLoading(false);
    }
  };

  const processWorkData = (workEntries, scheduleEntries) => {
    return workEntries.map((entry) => {
      // Find matching schedule item for this entry
      const matchingSchedule = scheduleEntries.find((scheduleItem) => {
        const scheduleDate = new Date(scheduleItem.startDate)
          .toISOString()
          .split("T")[0];

        // Check if the employee and date match
        if (
          scheduleItem.assignment?.employee?.name === entry.user &&
          scheduleDate === entry.date
        ) {
          return true;
        }
        return false;
      });

      // Check if the ANR matches the scheduled project
      const scheduledANR = matchingSchedule?.project?.ANR || null;
      const isMatchingProject = scheduledANR === entry.ANR;

      return {
        ...entry,
        scheduledANR: scheduledANR,
        isMatchingProject: isMatchingProject,
        mismatchFlag: scheduledANR && !isMatchingProject,
      };
    });
  };

  const calculateMatchStatistics = (data) => {
    const totalWithSchedule = data.filter((item) => item.scheduledANR).length;
    const matched = data.filter((item) => item.isMatchingProject).length;
    const mismatched = data.filter((item) => item.mismatchFlag).length;

    setMatchAnalysis({
      matched,
      mismatched,
      total: totalWithSchedule,
      matchPercentage:
        totalWithSchedule > 0
          ? Math.round((matched / totalWithSchedule) * 100)
          : 0,
    });
  };

  // Filter data based on selections
  const filteredData = workData.filter((item) => {
    const employeeMatch = selectedEmployee
      ? item.user === selectedEmployee
      : true;
    const anrMatch = selectedANR ? item.ANR === selectedANR : true;

    // Only show alerts if the option is enabled
    if (showAlerts && !item.mismatchFlag) {
      return false;
    }

    return employeeMatch && anrMatch;
  });

  // Helper to format time
  const formatTime = (time) => {
    if (!time) return "0h";
    return `${parseFloat(time).toFixed(2)}h`;
  };

  // Generate chart data for project work distribution
  const generateChartData = () => {
    const anrTotals = {};

    workData.forEach((item) => {
      const anr = item.ANR || "Unknown";
      if (!anrTotals[anr]) {
        anrTotals[anr] = {
          anr,
          totalHours: 0,
          matchedHours: 0,
          mismatchedHours: 0,
        };
      }

      const hours = parseFloat(item.TotalTime || 0);
      anrTotals[anr].totalHours += hours;

      if (item.isMatchingProject) {
        anrTotals[anr].matchedHours += hours;
      } else if (item.mismatchFlag) {
        anrTotals[anr].mismatchedHours += hours;
      } else {
        // No schedule found for this work entry
        anrTotals[anr].matchedHours += hours;
      }
    });

    return Object.values(anrTotals).sort((a, b) => b.totalHours - a.totalHours);
  };

  // Mock data generation function
  const generateMockData = () => {
    // Fallback employee list if fetched employees are not available
    const defaultEmployees = [
      "Johann Schmidt",
      "Emma Müller",
      "Michael Wagner",
      "Sophie Becker",
      "Thomas Schulz",
    ];

    const employeeNames =
      employees.length > 0
        ? employees.map((emp) => emp.name)
        : defaultEmployees;

    const projects = ["P1001", "P1002", "P1003", "P1004", "P1005"];
    const mockData = [];

    // Generate entries for the last 7 days
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];

      // Generate 3-5 entries per day
      const entriesCount = Math.floor(Math.random() * 3) + 3;

      for (let j = 0; j < entriesCount; j++) {
        const user =
          employeeNames[Math.floor(Math.random() * employeeNames.length)];
        const anr = projects[Math.floor(Math.random() * projects.length)];
        const scheduledANR =
          Math.random() > 0.3
            ? anr
            : projects[Math.floor(Math.random() * projects.length)];
        const isMatching = anr === scheduledANR;

        mockData.push({
          date: dateStr,
          user,
          ANR: anr,
          description: `Work on ${anr}`,
          StartTime: "08:00",
          EndTime: "16:00",
          TotalTime: (Math.random() * 6 + 2).toFixed(2),
          isOverTime: Math.random() > 0.8,
          scheduledANR,
          isMatchingProject: isMatching,
          mismatchFlag: !isMatching && scheduledANR,
        });
      }
    }

    return mockData;
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Live Work Tracking
            </h2>
            {matchAnalysis.mismatched > 0 && (
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {matchAnalysis.mismatched} Project Mismatch
                {matchAnalysis.mismatched !== 1 && "es"}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showAlerts"
                checked={showAlerts}
                onChange={(e) => setShowAlerts(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label
                htmlFor="showAlerts"
                className="ml-2 text-sm text-gray-700"
              >
                Show Alerts Only
              </label>
            </div>

            <button
              onClick={toggleExpand}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
            >
              {isExpanded ? "Collapse" : "Expand"}
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">
              Total Work Hours
            </h3>
            <p className="text-2xl font-bold text-indigo-600">
              {workData
                .reduce((sum, item) => sum + parseFloat(item.TotalTime || 0), 0)
                .toFixed(2)}
              h
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">
              Project Match Rate
            </h3>
            <p className="text-2xl font-bold text-green-600">
              {matchAnalysis.matchPercentage}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {matchAnalysis.matched} of {matchAnalysis.total} entries match
              scheduled projects
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">
              Mismatched Projects
            </h3>
            <p className="text-2xl font-bold text-orange-500">
              {matchAnalysis.mismatched}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">
              Active Projects
            </h3>
            <p className="text-2xl font-bold text-purple-600">
              {uniqueANRs.length}
            </p>
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-700">
              <p>{error}</p>
              <button
                onClick={fetchLiveWorkData}
                className="mt-2 inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="px-4 py-2 border-t border-gray-200">
                <div className="flex flex-wrap gap-4">
                  <div className="w-full md:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee
                    </label>
                    <select
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Employees</option>
                      {uniqueEmployees.map((emp) => (
                        <option key={emp.name} value={emp.name}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="w-full md:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Project (ANR)
                    </label>
                    <select
                      value={selectedANR}
                      onChange={(e) => setSelectedANR(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Projects</option>
                      {uniqueANRs.map((anr) => (
                        <option key={anr} value={anr}>
                          {anr}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="w-full md:w-auto flex items-end ml-auto">
                    <button
                      onClick={fetchLiveWorkData}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Refresh Data
                    </button>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="p-4 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Work Distribution by Project
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={generateChartData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="anr" />
                      <YAxis
                        label={{
                          value: "Hours",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip
                        formatter={(value) => `${value.toFixed(2)} hours`}
                      />
                      <Legend />
                      <Bar
                        dataKey="matchedHours"
                        name="Matched Work"
                        fill="#10B981"
                      />
                      <Bar
                        dataKey="mismatchedHours"
                        name="Mismatched Work"
                        fill="#F97316"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Work Entries Table */}
              <div className="p-4 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {showAlerts ? "Project Work Alerts" : "Work Entries"}
                </h3>

                {filteredData.length === 0 ? (
                  <div className="bg-gray-50 p-6 text-center text-gray-500 rounded-lg">
                    {showAlerts
                      ? "No project mismatches found for the selected filters."
                      : "No work entries found for the selected filters."}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Date
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Employee
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Actual Project
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Scheduled Project
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Time
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Description
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredData.map((entry, index) => (
                          <tr
                            key={index}
                            className={entry.mismatchFlag ? "bg-orange-50" : ""}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.user}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <span
                                className={
                                  entry.mismatchFlag
                                    ? "text-orange-600"
                                    : "text-gray-900"
                                }
                              >
                                {entry.ANR}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.scheduledANR || "Not Scheduled"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.StartTime} - {entry.EndTime}
                              <div className="text-xs text-gray-500">
                                {formatTime(entry.TotalTime)}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                              {entry.description || entry.Arbeiten || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {entry.mismatchFlag ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                                  Project Mismatch
                                </span>
                              ) : entry.isOverTime ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  Overtime
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Normal
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Planner2() {
  // State declarations

  const [ganttData, setGanttData] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [days, setDays] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("week"); // 'day', 'week', 'month', or 'gantt'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    employee: "",
    machine: "",
    status: "all",
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedGanttItem, setSelectedGanttItem] = useState(null);
  const [ganttDateRange, setGanttDateRange] = useState({
    start: null,
    end: null,
  });
  const [showGanttDetails, setShowGanttDetails] = useState(false);
  const [showLiveTracking, setShowLiveTracking] = useState(true);

  // Generate time slots for day view (30 minute intervals, 6:00 AM to 8:00 PM)
  useEffect(() => {
    if (viewMode === "day") {
      const slots = [];
      for (let hour = 6; hour <= 20; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          slots.push({ hour, minute });
        }
      }
      setTimeSlots(slots);
    }
  }, [viewMode]);

  // Set up Gantt date range
  useEffect(() => {
    if (viewMode === "gantt") {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);

      const endDate = new Date(today);

      endDate.setDate(today.getDate() + 21); // Show 4 weeks total

      setGanttDateRange({ start: startDate, end: endDate });
    }
  }, [viewMode]);

  // Ensure we always have at least a default set of days
  const safeDays =
    Array.isArray(days) && days.length > 0
      ? days
      : Array(7)
          .fill()
          .map((_, i) => {
            const day = new Date();
            day.setDate(day.getDate() + i);
            return day;
          });

  // Update days when view or current date changes
  useEffect(() => {
    updateDateRange(currentDate, viewMode);
  }, [currentDate, viewMode]);

  // Add this useEffect to fetch schedule data when the component loads or relevant state changes
  useEffect(() => {
    if (projects.length > 0 && days.length > 0) {
      console.log("Fetching schedule items with:", {
        projects: projects.length,
        days: days.length,
        viewMode,
      });
      fetchScheduleItems();
    }
  }, [days.length, projects.length, viewMode, currentDate]);

  // Debugging function that can be called to inspect state
  const debugState = () => {
    console.log("Current State:", {
      viewMode,
      currentDate: currentDate.toISOString(),
      days: days.map((d) => d.toISOString()),
      projects: projects.length,
      scheduleKeys: Object.keys(schedule),
      schedule,
    });
  };

  // Function to fetch schedule items from the database
  // Helper function to standardize date handling for API requests
  const formatDateForAPI = (date) => {
    // Ensure date is a proper Date object
    const d = new Date(date);

    // Return in format YYYY-MM-DD (no timezone component)
    return d.toISOString().split("T")[0];
  };

  // Helper function to standardize date handling for querying schedules
  const getDateRangeForQuery = (currentDate, viewMode) => {
    let startDate, endDate;

    // Create new date objects to avoid mutating original date
    if (viewMode === "day") {
      startDate = new Date(currentDate);
      endDate = new Date(currentDate);
    } else if (viewMode === "week") {
      startDate = new Date(currentDate);
      startDate.setDate(currentDate.getDate() - currentDate.getDay()); // Start from Sunday

      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6); // End on Saturday
    } else if (viewMode === "month") {
      startDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      endDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
    } else if (viewMode === "gantt") {
      startDate = ganttDateRange.start;
      endDate = ganttDateRange.end;
    }

    // Format dates as YYYY-MM-DD for API
    return {
      start: formatDateForAPI(startDate),
      end: formatDateForAPI(endDate),
    };
  };

  // Updated fetchScheduleItems function to use consistent date handling
  const fetchScheduleItems = async () => {
    try {
      setIsLoading(true);

      // Get properly formatted date range for current view
      const { start, end } = getDateRangeForQuery(currentDate, viewMode);

      // Call API to get schedule items
      const response = await fetch(`${scheduleAPI}?start=${start}&end=${end}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch schedule: ${response.status}`);
      }

      const result = await response.json();
      console.log("Schedule API response:", result); // Add logging

      if (result.success && result.data && Array.isArray(result.data)) {
        // Process schedule items and update state
        const newSchedule = {};

        // Generate the days array for the current view if needed
        if (days.length === 0) {
          updateDateRange(currentDate, viewMode);
        }

        console.log(
          "Days array:",
          days.map((d) => d.toISOString())
        ); // Add logging

        // Loop through schedule items and map them to the schedule object
        result.data.forEach((item) => {
          console.log("Processing schedule item:", item); // Add logging

          if (!item.assignment || !item.project) {
            console.log("Missing assignment or project:", item);
            return;
          }

          const assignmentId = item.assignment._id || item.assignment;
          const projectId = item.project._id || item.project;

          // Find the project details
          const projectDetails = projects.find((p) => p._id === projectId);
          if (!projectDetails) {
            console.log("Project details not found for ID:", projectId);
            return;
          }

          // Find which day index this item corresponds to
          const itemDate = new Date(item.startDate);
          console.log("Looking for day matching:", itemDate.toISOString());

          // More forgiving day matching approach
          const dayIndex = days.findIndex(
            (day) => formatDateForAPI(day) === formatDateForAPI(itemDate)
          );

          if (dayIndex === -1) {
            console.log(
              "No matching day found for:",
              formatDateForAPI(itemDate)
            );
            return;
          }

          console.log("Found matching day index:", dayIndex);

          // For day view with time slots
          if (viewMode === "day" && item.startTime) {
            // Parse time like "08:30" into hour and minute
            const [hour, minute] = item.startTime.split(":").map(Number);

            // Create the key in the same format used in handleDragEnd
            const cellKey = `${assignmentId}-${dayIndex}-${hour}-${minute}`;
            console.log("Added time slot schedule at key:", cellKey);

            // Add to schedule object
            newSchedule[cellKey] = {
              ...projectDetails,
              _scheduleId: item._id,
              timeSlot: { hour, minute },
            };
          } else {
            // For week/month views
            const cellKey = `${assignmentId}-${dayIndex}`;
            console.log("Added schedule at key:", cellKey);

            // Add to schedule object
            newSchedule[cellKey] = {
              ...projectDetails,
              _scheduleId: item._id,
            };
          }
        });

        console.log("New schedule state:", newSchedule);
        // Update the schedule state
        setSchedule(newSchedule);

        // If in Gantt view, also update Gantt data
        if (viewMode === "gantt") {
          fetchGanttData();
        }
      }
    } catch (error) {
      console.error("Error fetching schedule items:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Improved fetchGanttData function
  const fetchGanttData = async () => {
    if (viewMode !== "gantt") {
      return;
    }

    try {
      setIsLoading(true);

      if (!ganttDateRange.start || !ganttDateRange.end) {
        console.error("Gantt date range not initialized");
        return;
      }

      // Format dates for API
      const start = formatDateForAPI(ganttDateRange.start);
      const end = formatDateForAPI(ganttDateRange.end);

      console.log(`Fetching Gantt data from ${start} to ${end}`);

      const response = await fetch(
        `${scheduleAPI}/gantt?start=${start}&end=${end}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch Gantt data: ${response.status}`);
      }

      const result = await response.json();
      console.log("Gantt API response:", result);

      if (result.success && Array.isArray(result.data)) {
        // Convert API response to the format needed for the Gantt chart
        const formattedGanttData = result.data.map((item) => {
          // Get assignment from assignments array if possible
          const assignment = assignments.find(
            (a) => a._id === item.assignmentId
          ) || {
            _id: item.assignmentId,
            employee: { name: item.resource },
            machine: { name: item.machine },
          };

          // Get project from projects array if possible
          const project = projects.find((p) => p._id === item.projectId) || {
            _id: item.projectId,
            ANR: item.projectANR,
            Auftragsbezeichnung: item.task,
          };

          return {
            assignment,
            project,
            startDate: new Date(item.start),
            endDate: new Date(item.end),
            _scheduleId: item.id,
            status: item.status,
            progress: item.progress,
          };
        });

        console.log("Formatted Gantt data:", formattedGanttData);

        // Sort by assignment to group items together
        const sortedGanttData = _.groupBy(formattedGanttData, "assignment._id");

        // Convert to array format needed for rendering
        const ganttDisplayData = Object.keys(sortedGanttData).map(
          (assignmentId) => {
            const projectsForAssignment = sortedGanttData[assignmentId];
            const sampleItem = projectsForAssignment[0]; // We need one item to get the assignment info

            return {
              assignment: sampleItem.assignment,
              projects: projectsForAssignment.map((p) => ({
                project: p.project,
                startDate: p.startDate,
                endDate: p.endDate,
                _scheduleId: p._scheduleId,
                status: p.status,
                progress: p.progress,
              })),
            };
          }
        );

        console.log("Gantt display data:", ganttDisplayData);
        setGanttData(ganttDisplayData);
      } else {
        console.error("Invalid Gantt data structure received:", result);
        setGanttData([]);
      }
    } catch (error) {
      console.error("Error fetching Gantt data:", error);
      setGanttData([]);
    } finally {
      setIsLoading(false);
    }
  };
  const updateDateRange = (baseDate, mode) => {
    try {
      const newDays = [];
      const startDate = new Date(baseDate);

      if (mode === "day") {
        // Just show the current selected day
        newDays.push(new Date(baseDate));
      } else if (mode === "week") {
        // Start on Sunday
        startDate.setDate(baseDate.getDate() - baseDate.getDay());
        for (let i = 0; i < 7; i++) {
          const day = new Date(startDate);
          day.setDate(startDate.getDate() + i);
          newDays.push(day);
        }
      } else if (mode === "month") {
        // For month view, show entire month
        const year = baseDate.getFullYear();
        const month = baseDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          const day = new Date(year, month, i);
          newDays.push(day);
        }
      } else if (mode === "gantt") {
        // For Gantt view, we'll use the ganttDateRange state
        // No need to update days array as we handle differently
      }

      setDays(newDays);
    } catch (error) {
      console.error("Error updating date range:", error);
      // Fallback to one week
      const fallback = Array(7)
        .fill()
        .map((_, i) => {
          const day = new Date();
          day.setDate(day.getDate() + i);
          return day;
        });
      setDays(fallback);
    }
  };

  // Navigation functions
  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === "month") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === "gantt") {
      // Shift Gantt view back by 2 weeks
      const newStart = new Date(ganttDateRange.start);
      newStart.setDate(newStart.getDate() - 14);

      const newEnd = new Date(ganttDateRange.end);
      newEnd.setDate(newEnd.getDate() - 14);

      setGanttDateRange({ start: newStart, end: newEnd });
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === "month") {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === "gantt") {
      // Shift Gantt view forward by 2 weeks
      const newStart = new Date(ganttDateRange.start);
      newStart.setDate(newStart.getDate() + 14);

      const newEnd = new Date(ganttDateRange.end);
      newEnd.setDate(newEnd.getDate() + 14);

      setGanttDateRange({ start: newStart, end: newEnd });
    }
    setCurrentDate(newDate);
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
    if (viewMode === "gantt") {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);

      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 21); // Show 4 weeks total

      setGanttDateRange({ start: startDate, end: endDate });
    }
  };

  // Fetch assignments
  useEffect(() => {
    const fetchAssignments = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(assignmentsAPI);
        const data = await response.json();
        setAssignments(data);
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAssignments();
  }, []);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        let url = projectsAPI;
        if (searchTerm) {
          url += `?search=${encodeURIComponent(searchTerm)}`;
        } else {
          url += "?limit=20";
        }

        const response = await fetch(url);
        const data = await response.json();
        const projectList = data.data || [];
        setProjects(projectList);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, [searchTerm]);

  // Function to fetch Gantt data from the server
  useEffect(() => {
    if (viewMode === "gantt" && ganttDateRange.start && ganttDateRange.end) {
      console.log("Initializing Gantt data");
      fetchGanttData();
    }
  }, [viewMode, ganttDateRange.start, ganttDateRange.end]);

  // Make sure ganttDateRange is properly initialized
  useEffect(() => {
    if (
      viewMode === "gantt" &&
      (!ganttDateRange.start || !ganttDateRange.end)
    ) {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);

      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 21); // Show 4 weeks total

      console.log("Initializing Gantt date range");
      setGanttDateRange({ start: startDate, end: endDate });
    }
  }, [viewMode, ganttDateRange.start, ganttDateRange.end]);
  // onDragEnd handler using dnd-kit
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    // Check if dropped outside the projects container (id "PROJECTS")
    if (over.id !== "PROJECTS") {
      // Expect droppable id in the format "assignmentId-dayIndex" or "assignmentId-dayIndex-hour-minute" for day view
      const [assignmentId, dayIndex, hour, minute] = over.id.split("-");
      const droppedProject = projects.find((p) => String(p._id) === active.id);

      if (droppedProject) {
        try {
          setIsLoading(true);

          const selectedDay = days[dayIndex];
          if (!selectedDay) {
            console.error("Day not found for index:", dayIndex);
            return;
          }

          // Format dates for API using our helper
          const dateStr = formatDateForAPI(selectedDay);
          console.log("Saving schedule for date:", dateStr);

          // Create schedule item object based on the view mode
          let scheduleItem;

          if (viewMode === "day" && hour && minute) {
            // For day view with specific time slot
            const startTime = `${hour.toString().padStart(2, "0")}:${minute
              .toString()
              .padStart(2, "0")}`;
            // Default duration is 30 minutes
            const duration = 30;

            scheduleItem = {
              assignment: assignmentId,
              project: droppedProject._id,
              startDate: dateStr, // Use date string without time
              endDate: dateStr, // Use date string without time
              startTime,
              endTime: calculateEndTime(
                parseInt(hour),
                parseInt(minute),
                duration
              ),
              status: "scheduled",
              notes: `Scheduled from planner - ${droppedProject.ANR}`,
            };
          } else {
            // For week or month view (full day assignment)
            scheduleItem = {
              assignment: assignmentId,
              project: droppedProject._id,
              startDate: dateStr, // Use date string without time
              endDate: dateStr, // Use date string without time
              status: "scheduled",
              notes: `Scheduled from planner - ${droppedProject.ANR}`,
            };
          }

          console.log("Saving schedule item:", scheduleItem);

          // First update the local state for immediate UI feedback
          let cellKey;
          if (viewMode === "day" && hour && minute) {
            cellKey = `${assignmentId}-${dayIndex}-${hour}-${minute}`;
            setSchedule((prev) => ({
              ...prev,
              [cellKey]: {
                ...droppedProject,
                timeSlot: { hour: parseInt(hour), minute: parseInt(minute) },
              },
            }));
          } else {
            cellKey = `${assignmentId}-${dayIndex}`;
            setSchedule((prev) => ({
              ...prev,
              [cellKey]: droppedProject,
            }));
          }

          // Call API to save schedule item
          const response = await fetch(scheduleAPI, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(scheduleItem),
          });

          const result = await response.json();
          console.log("API response:", result);

          if (!response.ok) {
            throw new Error(
              `Error saving schedule item: ${result.message || "Unknown error"}`
            );
          }

          // Update the schedule with the returned item from the server
          // This ensures we have the correct _id and other server-generated fields
          setSchedule((prev) => ({
            ...prev,
            [cellKey]: {
              ...prev[cellKey],
              _scheduleId: result.data._id, // Store the schedule item ID for later updates/deletes
            },
          }));

          console.log(
            `Schedule item saved successfully with ID: ${result.data._id}`
          );

          // Refresh schedule data to ensure everything is in sync
          await fetchScheduleItems();
        } catch (error) {
          console.error("Failed to save schedule item:", error);
          // Show an error to the user
          alert("Failed to save schedule item: " + error.message);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  // Helper function to calculate end time (copied from your backend)
  const calculateEndTime = (hour, minute, durationInMinutes) => {
    let endHour = hour;
    let endMinute = minute + durationInMinutes;

    while (endMinute >= 60) {
      endHour++;
      endMinute -= 60;
    }

    // Format as HH:MM
    return `${endHour.toString().padStart(2, "0")}:${endMinute
      .toString()
      .padStart(2, "0")}`;
  };

  // Add this useEffect to fetch schedule items when component loads or view changes
  useEffect(() => {
    // Only fetch when we have projects and days available
    if (projects.length > 0 && (days.length > 0 || viewMode === "gantt")) {
      fetchScheduleItems();
    }
  }, [viewMode, currentDate, projects.length, days.length]);

  // Function to delete a scheduled item
  const deleteScheduleItem = async (scheduleId, cellKey) => {
    if (!scheduleId) {
      console.error("No schedule ID provided for deletion");
      return;
    }

    try {
      setDeleteLoading(true);

      console.log(`Deleting schedule item with ID: ${scheduleId}`);

      // Call API to delete schedule item
      const response = await fetch(`${scheduleAPI}/${scheduleId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error deleting schedule item:", errorData);
        alert(
          "Failed to delete schedule item: " +
            (errorData.message || "Unknown error")
        );
        return;
      }

      console.log("Delete successful, removing from local state:", cellKey);

      // Remove from local state
      setSchedule((prev) => {
        const newSchedule = { ...prev };
        delete newSchedule[cellKey];
        return newSchedule;
      });

      // Optionally refresh data to ensure everything is in sync
      await fetchScheduleItems();
    } catch (error) {
      console.error("Failed to delete schedule item:", error);
      alert("Failed to delete schedule item: " + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };
  // Component for displaying a scheduled project with delete button
  const ScheduledProject = ({ project, onDelete }) => {
    return (
      <div className="h-full p-2 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors duration-150 ease-in-out shadow-sm relative group">
        <div className="font-medium text-amber-700 text-sm mb-1 flex items-center">
          <svg
            className="h-3 w-3 mr-1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          {project.ANR}
        </div>
        <div className="text-xs text-gray-600 line-clamp-2 overflow-hidden text-ellipsis">
          {project.Auftragsbezeichnung}
        </div>

        {/* Delete Button - hidden by default, visible on hover */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-in-out p-1 rounded-full hover:bg-red-100 text-red-600"
          title="Remove from schedule"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    );
  };
  // Helper to format assignment information
  const formatResourceInfo = (assignment) => {
    const employeeName = assignment.employee?.name || "Unassigned";
    const machineName = assignment.machine?.name || "No Machine";
    const machineProcess = assignment.machine?.prozess || "";

    return (
      <div className="flex flex-col">
        <div className="font-semibold text-gray-800 mb-1">{employeeName}</div>
        <div className="text-sm text-gray-600">
          <div>{machineName}</div>
          {machineProcess && (
            <div className="text-gray-500">{machineProcess}</div>
          )}
        </div>
      </div>
    );
  };

  // Generate header title based on view mode and days
  const getHeaderTitle = () => {
    try {
      if (viewMode === "day" && safeDays.length > 0) {
        const formatter = new Intl.DateTimeFormat("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        return formatter.format(safeDays[0]);
      } else if (viewMode === "week" && safeDays.length > 0) {
        const firstDay = safeDays[0];
        const lastDay = safeDays[safeDays.length - 1];

        const formatter = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
        });

        return `${formatter.format(firstDay)} - ${formatter.format(
          lastDay
        )}, ${lastDay.getFullYear()}`;
      } else if (viewMode === "month") {
        return currentDate.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
      } else if (viewMode === "gantt") {
        // For Gantt view we show the date range
        const formatter = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
        });

        if (ganttDateRange.start && ganttDateRange.end) {
          return `${formatter.format(
            ganttDateRange.start
          )} - ${formatter.format(
            ganttDateRange.end
          )}, ${ganttDateRange.end.getFullYear()}`;
        } else {
          return "Production Timeline";
        }
      }
      return "Production Planner";
    } catch (error) {
      console.error("Error generating header title:", error);
      return "Production Planner";
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Filter assignments based on filter options
  const filteredAssignments = assignments.filter((assignment) => {
    const employeeMatch =
      !filterOptions.employee ||
      (assignment.employee?.name || "")
        .toLowerCase()
        .includes(filterOptions.employee.toLowerCase());

    const machineMatch =
      !filterOptions.machine ||
      (assignment.machine?.name || "")
        .toLowerCase()
        .includes(filterOptions.machine.toLowerCase());

    return employeeMatch && machineMatch;
  });

  // Generate Gantt data (example implementation - would be integrated with real data)
  const generateGanttData = () => {
    // In a real implementation, you would map real schedule data to this format
    const mockGanttData = [];

    // Get all unique assignments with projects in the schedule
    const assignmentProjects = {};

    Object.keys(schedule).forEach((key) => {
      const [assignmentId, dayIndex] = key.split("-");
      if (!assignmentProjects[assignmentId]) {
        assignmentProjects[assignmentId] = [];
      }

      const project = schedule[key];
      const day = days[dayIndex] || new Date(); // Fallback to current date if day not found

      // Check if this project is already added for this assignment
      const existingProject = assignmentProjects[assignmentId].find(
        (p) => p.project._id === project._id
      );

      if (!existingProject) {
        // Add new project entry
        assignmentProjects[assignmentId].push({
          project,
          startDate: new Date(day),
          // Randomly assign 1-5 days duration for demo purposes
          endDate: new Date(
            new Date(day).setDate(
              day.getDate() + Math.floor(Math.random() * 5) + 1
            )
          ),
        });
      }
    });

    // Convert to Gantt data format
    assignments.forEach((assignment) => {
      const assignmentId = assignment._id;
      const projects = assignmentProjects[assignmentId] || [];

      if (projects.length > 0) {
        mockGanttData.push({
          assignment,
          projects,
        });
      }
    });

    return mockGanttData;
  };

  // Calculate position and width for Gantt chart bars
  const calculateGanttPosition = (startDate, endDate) => {
    if (!ganttDateRange.start || !ganttDateRange.end)
      return { position: 0, width: 0 };

    const rangeStart = ganttDateRange.start.getTime();
    const rangeEnd = ganttDateRange.end.getTime();
    const totalDuration = rangeEnd - rangeStart;

    const projectStart = startDate.getTime();
    const projectEnd = endDate.getTime();

    // Calculate position as percentage from the start
    const position = ((projectStart - rangeStart) / totalDuration) * 100;

    // Calculate width as percentage of the total duration
    const duration = ((projectEnd - projectStart) / totalDuration) * 100;

    return { position, duration };
  };

  // Render the appropriate view based on viewMode
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center">
            <svg
              className="animate-spin h-10 w-10 text-indigo-500 mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-600">Loading assignments...</p>
          </div>
        </div>
      );
    }

    if (viewMode === "day") {
      // Day view with hourly slots
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200 min-w-[200px]"
                >
                  Resource
                </th>
                <th
                  scope="col"
                  className="bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                  style={{ minWidth: "800px" }}
                >
                  Schedule
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssignments.map((assignment) => {
                const rowId = assignment._id;
                return (
                  <tr key={rowId} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 px-4 py-4 whitespace-nowrap border-r border-gray-200 bg-white">
                      {formatResourceInfo(assignment)}
                    </td>
                    <td className="p-0 border-r border-gray-200">
                      <div className="max-h-[450px] overflow-y-auto">
                        {timeSlots.map((timeSlot, timeIndex) => {
                          const cellId = `${rowId}-0-${timeSlot.hour}-${timeSlot.minute}`;
                          const assignedProject = schedule[cellId];
                          return (
                            <TimeSlot
                              key={timeIndex}
                              hour={timeSlot.hour}
                              minutes={timeSlot.minute}
                              cellId={cellId}
                            >
                              {assignedProject && (
                                <ScheduledProject
                                  project={assignedProject}
                                  onDelete={() =>
                                    deleteScheduleItem(
                                      assignedProject._scheduleId,
                                      cellId
                                    )
                                  }
                                />
                              )}
                            </TimeSlot>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else if (viewMode === "gantt") {
      // If ganttData is empty, use generateGanttData as fallback
      const displayData =
        ganttData.length > 0 ? ganttData : generateGanttData();

      console.log("Rendering Gantt with data:", displayData);

      return (
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Gantt Header with dates */}
            <div className="flex border-b border-gray-200 bg-gray-50 py-2">
              <div className="w-64 flex-shrink-0 px-4 font-medium text-gray-500 border-r border-gray-200">
                Resource
              </div>
              <div className="flex-1 relative">
                {/* Date indicators */}
                {Array.from({ length: 28 }).map((_, index) => {
                  const date = new Date(ganttDateRange.start);
                  date.setDate(date.getDate() + index);
                  const isDateToday = isToday(date);

                  return (
                    <div
                      key={index}
                      className={`absolute top-0 bottom-0 border-l border-gray-200 ${
                        isDateToday ? "border-l-2 border-blue-500" : ""
                      }`}
                      style={{ left: `${(index / 28) * 100}%` }}
                    >
                      <div
                        className={`text-xs px-1 ${
                          isDateToday
                            ? "font-bold text-blue-600"
                            : "text-gray-500"
                        }`}
                      >
                        {date.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gantt Body with project bars */}
            <div className="divide-y divide-gray-200">
              {displayData.map((item, index) => (
                <div key={index} className="flex hover:bg-gray-50">
                  <div className="w-64 flex-shrink-0 p-4 border-r border-gray-200">
                    {formatResourceInfo(item.assignment)}
                  </div>
                  <div className="flex-1 p-4 relative h-16">
                    {item.projects.map((projectItem, projectIndex) => {
                      const { position, duration } = calculateGanttPosition(
                        projectItem.startDate,
                        projectItem.endDate
                      );

                      const isSelected =
                        selectedGanttItem &&
                        selectedGanttItem.project._id ===
                          projectItem.project._id &&
                        selectedGanttItem.assignment._id ===
                          item.assignment._id;

                      return (
                        <GanttBar
                          key={projectIndex}
                          project={projectItem.project}
                          startPosition={position}
                          duration={duration}
                          isSelected={isSelected}
                          onClick={() => {
                            setSelectedGanttItem({
                              project: projectItem.project,
                              assignment: item.assignment,
                              startDate: projectItem.startDate,
                              endDate: projectItem.endDate,
                              _scheduleId: projectItem._scheduleId,
                            });
                            setShowGanttDetails(true);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gantt Details Panel (when a project is selected) */}
          {showGanttDetails && selectedGanttItem && (
            <div className="fixed bottom-0 right-0 w-96 bg-white shadow-lg border border-gray-200 rounded-tl-lg p-4 z-20">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-lg text-gray-900">
                  Project Details
                </h3>
                <button
                  onClick={() => setShowGanttDetails(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Project ANR
                  </h4>
                  <p className="text-gray-900">
                    {selectedGanttItem.project.ANR}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Description
                  </h4>
                  <p className="text-gray-900">
                    {selectedGanttItem.project.Auftragsbezeichnung}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Quantity
                  </h4>
                  <p className="text-gray-900">
                    {selectedGanttItem.project.Menge}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      Start Date
                    </h4>
                    <p className="text-gray-900">
                      {selectedGanttItem.startDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      End Date
                    </h4>
                    <p className="text-gray-900">
                      {selectedGanttItem.endDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Assigned To
                  </h4>
                  <p className="text-gray-900">
                    {selectedGanttItem.assignment.employee?.name ||
                      "Unassigned"}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">Machine</h4>
                  <p className="text-gray-900">
                    {selectedGanttItem.assignment.machine?.name || "No Machine"}
                  </p>
                </div>

                <div className="pt-2 flex space-x-2">
                  <button className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      // Delete the schedule item
                      if (selectedGanttItem._scheduleId) {
                        deleteScheduleItem(selectedGanttItem._scheduleId);
                        // Close the details panel
                        setShowGanttDetails(false);
                        // Refresh the Gantt data
                        fetchGanttData();
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-white text-red-700 text-sm font-medium rounded-md border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    } else if (viewMode === "week" || viewMode === "month") {
      // Week or Month view
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200 min-w-[200px]"
                >
                  Resource
                </th>
                {safeDays.map((day, index) => (
                  <th
                    key={index}
                    scope="col"
                    className={`px-3 py-3 text-center text-xs font-medium uppercase tracking-wider border-b border-gray-200 ${
                      isToday(day)
                        ? "bg-blue-50 text-blue-700"
                        : "bg-gray-50 text-gray-500"
                    }`}
                    style={{
                      minWidth: viewMode === "week" ? "160px" : "120px",
                    }}
                  >
                    <div className="font-semibold">
                      {day.toLocaleDateString(undefined, { weekday: "short" })}
                    </div>
                    <div>
                      {day.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                      })}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssignments.map((assignment) => {
                const rowId = assignment._id;
                return (
                  <tr key={rowId} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 px-4 py-4 whitespace-nowrap border-r border-gray-200 bg-white">
                      {formatResourceInfo(assignment)}
                    </td>
                    {safeDays.map((day, dayIndex) => {
                      const cellId = `${rowId}-${dayIndex}`;
                      const assignedProject = schedule[cellId];
                      return (
                        <td
                          key={cellId}
                          className={`px-1 py-1 border-r border-gray-200 ${
                            isToday(day) ? "bg-blue-50/20" : ""
                          }`}
                        >
                          <DroppableCell cellId={cellId} viewMode={viewMode}>
                            {assignedProject && (
                              <ScheduledProject
                                project={assignedProject}
                                onDelete={() =>
                                  deleteScheduleItem(
                                    assignedProject._scheduleId,
                                    cellId
                                  )
                                }
                              />
                            )}
                          </DroppableCell>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  // Toggle live tracking visibility
  const toggleLiveTracking = () => {
    setShowLiveTracking(!showLiveTracking);
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <svg
                  className="h-8 w-8 text-indigo-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h1 className="ml-3 text-2xl font-semibold text-gray-900">
                  Production Planner
                </h1>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleLiveTracking}
                  className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 ${
                    showLiveTracking
                      ? "border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                      : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                  }`}
                >
                  {showLiveTracking
                    ? "Hide Live Tracking"
                    : "Show Live Tracking"}
                </button>
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
                >
                  {sidebarCollapsed ? "Show Projects" : "Hide Projects"}
                </button>

                <div className="relative">
                  <select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    className="appearance-none h-10 px-4 py-2 pl-3 pr-8 border border-gray-300 bg-white rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="day">Day View</option>
                    <option value="week">Week View</option>
                    <option value="month">Month View</option>
                    <option value="gantt">Gantt View</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Date navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center bg-white rounded-lg shadow-sm p-4 mb-6">
            <button
              onClick={navigatePrevious}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
            >
              <svg
                className="mr-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Previous
            </button>

            <h2 className="text-xl font-medium text-gray-900">
              {getHeaderTitle()}
            </h2>

            <div className="flex space-x-2">
              <button
                onClick={navigateToday}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
              >
                Today
              </button>
              <button
                onClick={navigateNext}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
              >
                Next
                <svg
                  className="ml-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Live Work Tracking Component */}
          {showLiveTracking && (
            <LiveWorkTracking currentDate={currentDate} viewMode={viewMode} />
          )}

          {/* Filter section */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-grow max-w-xs">
                <label
                  htmlFor="employee-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Employee
                </label>
                <input
                  id="employee-filter"
                  type="text"
                  placeholder="Filter by employee..."
                  value={filterOptions.employee}
                  onChange={(e) =>
                    setFilterOptions({
                      ...filterOptions,
                      employee: e.target.value,
                    })
                  }
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md shadow-sm"
                />
              </div>

              <div className="flex-grow max-w-xs">
                <label
                  htmlFor="machine-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Machine
                </label>
                <input
                  id="machine-filter"
                  type="text"
                  placeholder="Filter by machine..."
                  value={filterOptions.machine}
                  onChange={(e) =>
                    setFilterOptions({
                      ...filterOptions,
                      machine: e.target.value,
                    })
                  }
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="flex gap-6">
            {/* Projects Sidebar - Hide in Gantt view */}
            <div
              className={viewMode === "gantt" ? "hidden" : "w-72 flex-shrink-0"}
              style={
                sidebarCollapsed
                  ? { opacity: 0, pointerEvents: "none", position: "absolute" }
                  : {}
              }
            >
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">
                    Available Projects
                  </h3>
                  <div className="text-xs text-gray-500 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
                      <span>Is planned</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
                      <span>Not planned</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md shadow-sm"
                  />
                </div>

                {/* Projects droppable area */}
                <DroppableProjects>
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <DraggableProject
                        key={String(project._id)}
                        project={project}
                      />
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerm
                        ? "No matching projects found"
                        : isLoading
                        ? "Loading projects..."
                        : "No projects available"}
                    </div>
                  )}
                </DroppableProjects>
              </div>
            </div>

            {/* Main Planner Grid */}
            <div
              className={`${
                viewMode === "gantt" || sidebarCollapsed ? "w-full" : "flex-1"
              } bg-white rounded-lg shadow-sm overflow-hidden`}
            >
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}

export default Planner2;
